import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  ProductSelector,
  VariantSelector,
  PriceSelector,
  OfferSelector,
  OrderBumpSelector,
  ProductVariantSelector,
} from '@/components/resource-pickers';
import type { ProductVariantSelection } from '@/components/resource-pickers';
import {
  useTagsFilterOptions,
  useBrowserFilterOptions,
  useCountryFilterOptions,
  useContinentFilterOptions,
  useLocaleFilterOptions,
  useUtmSourceFilterOptions,
} from '@/lib/api/devices';
import { CountrySelector } from '@/components/country-selector';
import type { ConditionParam, ConditionParams } from '../types/conditions';

interface ConditionParamsEditorProps {
  params: ConditionParam[];
  values: ConditionParams;
  onChange: (values: ConditionParams) => void;
  storeId?: string; // For resource pickers
  readOnly?: boolean;
}

interface TagOption {
  label: string;
  value: string;
}

/**
 * Canonical payment-method options exposed by the funnel orchestrator's
 * `payment.usedMethod` condition. The `value` strings match exactly what
 * studio + native-checkout v2 emit on `data.payment.method` at PAYMENT_SUCCESS
 * dispatch — keep this list in sync when new methods land in those templates.
 */
const PAYMENT_METHOD_OPTIONS: TagOption[] = [
  { value: 'card', label: 'Credit Card' },
  { value: 'apple_pay', label: 'Apple Pay' },
  { value: 'google_pay', label: 'Google Pay' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'klarna', label: 'Klarna' },
  { value: 'afterpay', label: 'Afterpay' },
  { value: 'affirm', label: 'Affirm' },
  { value: 'bancontact', label: 'Bancontact' },
  { value: 'ideal', label: 'iDEAL' },
  { value: 'twint', label: 'TWINT' },
  { value: 'blik', label: 'BLIK' },
  { value: 'skrill', label: 'Skrill' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'custom_payment', label: 'Custom Payment' },
  { value: 'hipay', label: 'HiPay' },
  { value: 'whop', label: 'Whop' },
  { value: 'stripe_express', label: 'Stripe Express' },
];

/**
 * Tag Selector Component (Single Select)
 * Shows available tags in a searchable dropdown with formatted labels
 */
function TagSelector({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const [open, setOpen] = React.useState(false);
  const { options, loading } = useTagsFilterOptions();

  // Extract unique prefixes from available tags
  const prefixes = React.useMemo(() => {
    const uniquePrefixes = new Set<string>();
    options.forEach((opt: TagOption) => {
      if (opt.value.includes(':')) {
        const prefix = opt.value.split(':')[0];
        uniquePrefixes.add(prefix);
      }
    });
    return Array.from(uniquePrefixes).sort();
  }, [options]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-9 w-full min-w-0 justify-between overflow-hidden border-purple-200 bg-white text-sm font-normal"
        >
          {value ? (
            <span className="min-w-0 truncate">
              {options.find((opt: TagOption) => opt.value === value)?.label || value}
            </span>
          ) : (
            <span className="text-muted-foreground truncate">Select tag...</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search tags..." className="h-9 text-sm" />
          <CommandList>
            <CommandEmpty>
              {loading ? 'Loading tags...' : 'No tags found. Type a custom tag below.'}
            </CommandEmpty>

            {/* Show available prefixes if any */}
            {prefixes.length > 0 && (
              <CommandGroup heading="Common Prefixes">
                {prefixes.map((prefix) => (
                  <CommandItem
                    key={`prefix-${prefix}`}
                    value={prefix}
                    onSelect={() => {
                      onChange(prefix);
                      setOpen(false);
                    }}
                    className="text-muted-foreground text-xs"
                  >
                    <Badge variant="outline" className="mr-2">
                      {prefix}:
                    </Badge>
                    <span className="capitalize">{prefix.replace(/_/g, ' ')}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Show all available tags */}
            {options.length > 0 && (
              <CommandGroup heading="Available Tags">
                {options.map((option: TagOption) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={(currentValue) => {
                      onChange(currentValue === value ? '' : currentValue);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn('mr-2 h-4 w-4', value === option.value ? 'opacity-100' : 'opacity-0')}
                    />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{option.label}</span>
                      <span className="text-muted-foreground text-xs">{option.value}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
        {/* Allow custom tag input */}
        <div className="bg-muted/50 border-t p-2">
          <div className="text-muted-foreground mb-1 text-[10px] font-medium">Custom Tag</div>
          <Input
            placeholder="e.g., segment:vip or utm_source:facebook"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="h-8 text-xs"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setOpen(false);
              }
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Multi Tag Selector Component (for hasAnyTag, hasAllTags)
 * Allows selecting multiple tags, stores as comma-separated string
 */
function MultiTagSelector({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const [open, setOpen] = React.useState(false);
  const { options, loading } = useTagsFilterOptions();

  // Parse comma-separated string to array
  const selectedTags = React.useMemo(() => {
    return value
      ? value
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
      : [];
  }, [value]);

  // Toggle tag selection
  const toggleTag = (tag: string) => {
    const newTags = selectedTags.includes(tag)
      ? selectedTags.filter((t) => t !== tag)
      : [...selectedTags, tag];
    onChange(newTags.join(', '));
  };

  // Remove a specific tag
  const removeTag = (tagToRemove: string) => {
    const newTags = selectedTags.filter((t) => t !== tagToRemove);
    onChange(newTags.join(', '));
  };

  return (
    <div className="w-full min-w-0 space-y-2 overflow-hidden">
      {/* Selected Tags Display */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedTags.map((tag) => (
            <Badge key={tag} variant="secondary" className="max-w-full gap-1 pr-1">
              <span className="truncate text-xs">
                {options.find((opt: TagOption) => opt.value === tag)?.label || tag}
              </span>
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="hover:bg-muted ml-1 shrink-0 rounded-sm"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Dropdown Selector */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="h-9 w-full min-w-0 justify-between overflow-hidden border-purple-200 bg-white text-sm font-normal"
          >
            <span className="text-muted-foreground truncate">
              {selectedTags.length > 0
                ? `${selectedTags.length} tag${selectedTags.length > 1 ? 's' : ''} selected`
                : 'Select tags...'}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[340px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search tags..." className="h-9 text-sm" />
            <CommandList>
              <CommandEmpty>
                {loading ? 'Loading tags...' : 'No tags found. Type custom tags below.'}
              </CommandEmpty>

              {/* Show all available tags with checkboxes */}
              {options.length > 0 && (
                <CommandGroup heading={`Available Tags (${selectedTags.length} selected)`}>
                  {options.map((option: TagOption) => {
                    const isSelected = selectedTags.includes(option.value);
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        onSelect={() => {
                          toggleTag(option.value);
                        }}
                      >
                        <div
                          className={cn(
                            'border-primary mr-2 flex h-4 w-4 items-center justify-center rounded-sm border',
                            isSelected
                              ? 'bg-primary text-primary-foreground'
                              : 'opacity-50 [&_svg]:invisible',
                          )}
                        >
                          <Check className="h-4 w-4" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">{option.label}</span>
                          <span className="text-muted-foreground text-xs">{option.value}</span>
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
          {/* Allow custom comma-separated input */}
          <div className="bg-muted/50 border-t p-2">
            <div className="text-muted-foreground mb-1 text-[10px] font-medium">
              Custom Tags (comma-separated)
            </div>
            <Input
              placeholder="e.g., segment:vip, utm_source:facebook"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="h-8 text-xs"
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Generic Dropdown Selector Component
 * Reusable dropdown for any type of option (country, continent, browser, locale, utm_source)
 */
function GenericSelector({
  value,
  onChange,
  options,
  loading,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  customInputLabel,
  customInputPlaceholder,
}: {
  value: string;
  onChange: (value: string) => void;
  options: TagOption[];
  loading: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  customInputLabel?: string;
  customInputPlaceholder?: string;
}) {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-9 w-full min-w-0 justify-between overflow-hidden border-purple-200 bg-white text-sm font-normal"
        >
          {value ? (
            <span className="min-w-0 truncate">
              {options.find((opt) => opt.value === value)?.label || value}
            </span>
          ) : (
            <span className="text-muted-foreground truncate">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-9 text-sm" />
          <CommandList>
            <CommandEmpty>{loading ? 'Loading options...' : 'No options found.'}</CommandEmpty>

            {/* Show all available options */}
            {options.length > 0 && (
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={`${option.value} ${option.label}`}
                    onSelect={() => {
                      onChange(option.value === value ? '' : option.value);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn('mr-2 h-4 w-4', value === option.value ? 'opacity-100' : 'opacity-0')}
                    />
                    <span className="text-sm">{option.label}</span>
                    <span className="text-muted-foreground ml-2 text-xs">({option.value})</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
        {/* Allow custom input if label provided */}
        {customInputLabel && (
          <div className="bg-muted/50 border-t p-2">
            <div className="text-muted-foreground mb-1 text-[10px] font-medium">{customInputLabel}</div>
            <Input
              placeholder={customInputPlaceholder}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="h-8 text-xs"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setOpen(false);
                }
              }}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Component to edit parameters for a condition
 * Dynamically renders input fields or resource pickers based on param type
 */
export function ConditionParamsEditor({ params, values, onChange, storeId }: ConditionParamsEditorProps) {
  // ✅ Call all hooks at the top level (React requirement)
  const countryOptions = useCountryFilterOptions();
  const continentOptions = useContinentFilterOptions();
  const localeOptions = useLocaleFilterOptions();
  const browserOptions = useBrowserFilterOptions();
  const utmSourceOptions = useUtmSourceFilterOptions();

  if (!params || params.length === 0) {
    return null;
  }

  // Handle param value change
  const handleChange = (paramName: string, value: unknown) => {
    onChange({
      ...values,
      [paramName]: value,
    });
  };

  // Render appropriate input/picker based on param type
  const renderParamInput = (param: ConditionParam) => {
    const rawValue = values[param.name] ?? '';
    // Convert value to string for string-based inputs
    const stringValue = typeof rawValue === 'string' ? rawValue : String(rawValue || '');

    // ✅ Payment-method picker — matches the canonical method strings emitted
    // by studio + native-checkout v2 (see getAvailableConditions() entry for
    // `payment.usedMethod`). Keyed by `param.type === 'paymentMethod'` rather
    // than the param name so the same widget can be reused if a future
    // condition exposes a method param under a different field name.
    if (param.type === 'paymentMethod') {
      return (
        <GenericSelector
          value={stringValue}
          onChange={(val) => handleChange(param.name, val)}
          options={PAYMENT_METHOD_OPTIONS}
          loading={false}
          placeholder="Select payment method..."
          searchPlaceholder="Search payment methods..."
        />
      );
    }

    // ✅ Special handling for tag parameters
    // Use multi-select for "tags" (plural), single-select for "tag" (singular) or "prefix"
    if (param.name.toLowerCase() === 'tags') {
      // Multi-select for hasAnyTag, hasAllTags (expects comma-separated list)
      return <MultiTagSelector value={stringValue} onChange={(val) => handleChange(param.name, val)} />;
    }

    if (param.name.toLowerCase() === 'tag' || param.name.toLowerCase() === 'prefix') {
      // Single-select for hasTag, hasTagPrefix
      return <TagSelector value={stringValue} onChange={(val) => handleChange(param.name, val)} />;
    }

    // ✅ Geo/Device selectors (extract from customer tags)
    if (param.name.toLowerCase() === 'country') {
      return (
        <CountrySelector
          value={stringValue}
          onChange={(val) => handleChange(param.name, val)}
          placeholder="Select country..."
          searchPlaceholder="Search countries..."
        />
      );
    }

    if (param.name.toLowerCase() === 'continent') {
      return (
        <GenericSelector
          value={stringValue}
          onChange={(val) => handleChange(param.name, val)}
          options={continentOptions.options}
          loading={continentOptions.loading}
          placeholder="Select continent..."
          searchPlaceholder="Search continents..."
          customInputLabel="Custom Continent Code"
          customInputPlaceholder="e.g., EU, NA, AS"
        />
      );
    }

    if (param.name.toLowerCase() === 'locale') {
      return (
        <GenericSelector
          value={stringValue}
          onChange={(val) => handleChange(param.name, val)}
          options={localeOptions.options}
          loading={localeOptions.loading}
          placeholder="Select locale..."
          searchPlaceholder="Search locales..."
          customInputLabel="Custom Locale"
          customInputPlaceholder="e.g., en-US, fr-FR, pt-BR"
        />
      );
    }

    if (param.name.toLowerCase() === 'browser') {
      return (
        <GenericSelector
          value={stringValue}
          onChange={(val) => handleChange(param.name, val)}
          options={browserOptions.options}
          loading={browserOptions.loading}
          placeholder="Select browser..."
          searchPlaceholder="Search browsers..."
          customInputLabel="Custom Browser"
          customInputPlaceholder="e.g., chrome, firefox, safari"
        />
      );
    }

    if (param.name.toLowerCase() === 'source' && param.description?.toLowerCase().includes('utm')) {
      return (
        <GenericSelector
          value={stringValue}
          onChange={(val) => handleChange(param.name, val)}
          options={utmSourceOptions.options}
          loading={utmSourceOptions.loading}
          placeholder="Select UTM source..."
          searchPlaceholder="Search sources..."
          customInputLabel="Custom UTM Source"
          customInputPlaceholder="e.g., facebook, google, email"
        />
      );
    }

    // ✅ Unified Product/Variant selector (new format)
    if (param.type === 'productVariant' || param.name.toLowerCase() === 'selection') {
      return (
        <ProductVariantSelector
          value={rawValue as ProductVariantSelection | undefined}
          onChange={(val) => handleChange(param.name, val)}
          storeId={storeId}
          placeholder="Select product or variants..."
        />
      );
    }

    // Legacy: Resource pickers for specific types
    if (param.type === 'product' || param.name.toLowerCase().includes('productid')) {
      return (
        <ProductSelector
          value={stringValue}
          onChange={(val) => handleChange(param.name, val)}
          storeId={storeId}
          searchable={true}
          placeholder={`Select product...`}
        />
      );
    }

    if (param.type === 'variant' || param.name.toLowerCase().includes('variantid')) {
      return (
        <VariantSelector
          value={stringValue}
          onChange={(val) => handleChange(param.name, val)}
          storeId={storeId}
        />
      );
    }

    if (param.type === 'price' || param.name.toLowerCase().includes('priceid')) {
      return (
        <PriceSelector
          value={stringValue}
          onChange={(val) => handleChange(param.name, val)}
          storeId={storeId}
        />
      );
    }

    if (param.type === 'offer' || param.name.toLowerCase().includes('offerid')) {
      return (
        <OfferSelector
          value={stringValue}
          onChange={(val) => handleChange(param.name, val)}
          storeId={storeId}
        />
      );
    }

    if (param.type === 'orderBump' || param.name.toLowerCase().includes('orderbump')) {
      return (
        <OrderBumpSelector
          value={stringValue}
          onChange={(val) => handleChange(param.name, val)}
          storeId={storeId}
        />
      );
    }

    // Standard inputs for other types
    if (param.type === 'number') {
      const numValue = typeof rawValue === 'number' ? rawValue : parseFloat(stringValue) || 0;
      return (
        <Input
          id={param.name}
          type="number"
          value={numValue}
          onChange={(e) => handleChange(param.name, parseFloat(e.target.value))}
          placeholder={`Enter ${param.name}`}
          className="h-9 border-purple-200 bg-white"
          required={param.required}
        />
      );
    }

    if (param.type === 'boolean') {
      return (
        <div className="flex items-center gap-2">
          <input
            id={param.name}
            type="checkbox"
            checked={rawValue === true || rawValue === 'true'}
            onChange={(e) => handleChange(param.name, e.target.checked)}
            className="h-4 w-4 rounded border-purple-200"
          />
          <Label htmlFor={param.name} className="text-xs font-normal">
            Enable
          </Label>
        </div>
      );
    }

    // Default text input
    return (
      <Input
        id={param.name}
        type="text"
        value={stringValue}
        onChange={(e) => handleChange(param.name, e.target.value)}
        placeholder={`Enter ${param.name}`}
        className="h-9 border-purple-200 bg-white"
        required={param.required}
      />
    );
  };

  return (
    <div className="w-full min-w-0 space-y-3 overflow-hidden rounded-lg border border-purple-200 bg-purple-50 p-3">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-purple-900">Parameters</span>
        {params.filter((p) => p.required).length > 0 && (
          <Badge variant="secondary" className="h-4 bg-purple-200 text-[10px] text-purple-800">
            {params.filter((p) => p.required).length} required
          </Badge>
        )}
      </div>

      {params.map((param) => (
        <div key={param.name} className="w-full min-w-0">
          <Label htmlFor={param.name} className="mb-1 block truncate text-xs font-medium text-purple-900">
            {param.name}
            {param.required && <span className="ml-1 text-red-600">*</span>}
          </Label>
          <p className="mb-1.5 truncate text-[10px] text-purple-700">{param.description}</p>

          {renderParamInput(param)}
        </div>
      ))}
    </div>
  );
}
